/**
 * 任意テキストテンプレートマスタ (FreeTextTemplateMaster) の画面用定義とヘルパー。
 *
 * - 「全線区共通」を表す route_id (`__all__`) と、表示名 / 並び替え / プルダウン用の整形をまとめる。
 * - 本文の検証規則の正源はサーバ側 (`lambda/control-panel-api/index.py`)。
 *   ここでは文字数の上限値だけを画面のカウンタ用に持つ (二重実装になる検証は持たない)。
 */
import type { FreeTextTemplate } from '../api/types';

import type { RouteMaster } from './routes';

/**
 * 全線区共通のテンプレートを表す route_id。
 *
 * ⚠️ `lambda/control-panel-api/index.py` の `FREE_TEXT_TEMPLATE_ALL_ROUTES` と同じ値に保つこと。
 *    `hooks/useRouteSelection` の `ALL_ROUTES` (画面上の「(全線区)」= 絞り込みなし) とは
 *    **別の意味** なので混同しないこと (こちらは DB に保存される値)。
 */
export const FREE_TEXT_TEMPLATE_ALL_ROUTES = '__all__';

/** 全線区共通の表示名 */
export const FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL = '全線区共通';

/**
 * 本文の最大文字数。
 * ⚠️ `lambda/control-panel-api/index.py` の `MAX_FREE_TEXT_TEMPLATE_TEXT_LENGTH` と一致させる。
 * 放送地点マスタの「放送文面 (通常テキスト)」の上限 (`utils/broadcastContents.ts` の
 * `MAX_FREE_TEXT_LENGTH`) と同じ値に保つ (直接入力できる文面が登録できない状態を作らない)。
 */
export const MAX_FREE_TEXT_TEMPLATE_TEXT_LENGTH = 3000;

/**
 * タイトル / 備考の最大文字数 (管理用の項目で、放送内容には影響しない)。
 * ⚠️ `lambda/control-panel-api/index.py` の `MAX_TEMPLATE_TITLE_LENGTH` / `MAX_TEMPLATE_NOTE_LENGTH` と一致させる。
 */
export const MAX_TEMPLATE_TITLE_LENGTH = 100;
export const MAX_TEMPLATE_NOTE_LENGTH = 1000;

/** タイトルを持たない既存行の表示名 */
export const TEMPLATE_TITLE_UNSET_LABEL = '(タイトル未設定)';

/** route_id の表示名 (全線区共通 / 線区名。マスタに無い route_id はそのまま返す)。 */
export function freeTextTemplateRouteLabel(
  routeId: string,
  routes: readonly RouteMaster[],
): string {
  if (routeId === FREE_TEXT_TEMPLATE_ALL_ROUTES) return FREE_TEXT_TEMPLATE_ALL_ROUTES_LABEL;
  return routes.find((r) => r.route_id === routeId)?.route_name ?? routeId;
}

/** 日本語の辞書順 (あいうえお順) 比較器 */
const JA_COLLATOR = new Intl.Collator('ja');

/**
 * 一覧用の並び替え。全線区共通 → 各線区 (線区マスタの並び) → 本文のあいうえお順。
 * 元の配列は変更しない。
 */
export function sortFreeTextTemplates(
  templates: readonly FreeTextTemplate[],
  routes: readonly RouteMaster[],
): FreeTextTemplate[] {
  const routeOrder = (routeId: string): number => {
    if (routeId === FREE_TEXT_TEMPLATE_ALL_ROUTES) return -1;
    const index = routes.findIndex((r) => r.route_id === routeId);
    // マスタに無い route_id は末尾へ
    return index >= 0 ? index : Number.MAX_SAFE_INTEGER;
  };
  return [...templates].sort(
    (a, b) =>
      routeOrder(a.route_id) - routeOrder(b.route_id) ||
      a.route_id.localeCompare(b.route_id) ||
      JA_COLLATOR.compare(a.text, b.text) ||
      (a.created_at ?? '').localeCompare(b.created_at ?? ''),
  );
}

/**
 * 放送文面の選択肢に出す「全線区共通 → 選択線区」の順のグループ。
 *
 * - 全線区共通を先頭、その下に `routeId` の線区を置く。
 * - テンプレートが 0 件のグループは含めない。
 * - `routeId` が未指定 (空) のときは全線区共通のみ。
 * - 同じグループ内の並びは入力 (= API の並び) を保つ。
 */
export interface FreeTextTemplateGroup {
  /** `__all__` または線区の route_id */
  routeId: string;
  templates: FreeTextTemplate[];
}

export function groupFreeTextTemplatesForRoute(
  templates: readonly FreeTextTemplate[],
  routeId: string | undefined,
): FreeTextTemplateGroup[] {
  const groups: FreeTextTemplateGroup[] = [
    {
      routeId: FREE_TEXT_TEMPLATE_ALL_ROUTES,
      templates: templates.filter((t) => t.route_id === FREE_TEXT_TEMPLATE_ALL_ROUTES),
    },
  ];
  if (routeId && routeId !== FREE_TEXT_TEMPLATE_ALL_ROUTES) {
    groups.push({
      routeId,
      templates: templates.filter((t) => t.route_id === routeId),
    });
  }
  return groups.filter((g) => g.templates.length > 0);
}
